// src/modules/Admin/pages/Dashboard/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { supabase } from '../../../../config/supabase';
import {
  FaNewspaper,
  FaUsers,
  FaChartLine,
  FaEye,
  FaArrowRight,
  FaPlus
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  margin-bottom: 32px;
`;

const WelcomeTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin-bottom: 8px;
`;

const WelcomeText = styled.p`
  color: #666;
  font-size: 15px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${props => props.$color || '#f5f5f5'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
`;

const StatTrend = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  background: ${props => props.$positive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  padding: 4px 8px;
  border-radius: 4px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #111;
`;

const CardHeaderLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;
  text-decoration: none;
  transition: color 200ms;

  &:hover {
    color: #111;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #999;
  font-size: 24px;
`;

const EmptyTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: #666;
  margin-bottom: 20px;
`;

const CreateButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #111;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  transition: background 200ms;

  &:hover {
    background: #333;
  }
`;

const QuickActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuickAction = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  text-decoration: none;
  transition: all 200ms;

  &:hover {
    background: #f0f0f0;
    transform: translateX(4px);
  }
`;

const QuickActionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$color || '#e5e5e5'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
`;

const QuickActionContent = styled.div`
  flex: 1;

  .title {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 2px;
  }

  .desc {
    font-size: 12px;
    color: #666;
  }
`;

const QuickActionArrow = styled.div`
  color: #ccc;
  font-size: 14px;
`;

const RecentPostsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const RecentPostItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f5f5f5;
  text-decoration: none;
  transition: all 200ms;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const PostThumbnail = styled.div`
  width: 56px;
  height: 42px;
  border-radius: 6px;
  background: #f0f0f0;
  background-image: ${props => props.$image ? `url(${props.$image})` : 'none'};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const PostDetails = styled.div`
  flex: 1;
  min-width: 0;

  .title {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 4px;
  }

  .meta {
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const PostStatus = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 500;
  border-radius: 10px;
  background: ${props => props.$status === 'published' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)'};
  color: ${props => props.$status === 'published' ? '#10b981' : '#f59e0b'};
`;

const Dashboard = () => {
  const { user } = useAuth();
  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || 'Usuário';

  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState({
    postsCount: 0,
    totalViews: 0,
    leadsCount: 0,
    conversionRate: 0,
  });
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch posts
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('id, title, excerpt, cover_image, status, views, created_at')
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;

      // Fetch leads
      const { data: leads, error: leadsError } = await supabase
        .from('leads')
        .select('id, status');

      if (leadsError) throw leadsError;

      // Calculate stats
      const publishedPosts = posts?.filter(p => p.status === 'published') || [];
      const totalViews = publishedPosts.reduce((sum, post) => sum + (post.views || 0), 0);
      const totalLeads = leads?.length || 0;
      const convertedLeads = leads?.filter(l => l.status === 'converted').length || 0;
      const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100).toFixed(1) : 0;

      setStatsData({
        postsCount: publishedPosts.length,
        totalViews,
        leadsCount: totalLeads,
        conversionRate,
      });

      // Recent posts (last 5)
      setRecentPosts(posts?.slice(0, 5) || []);

    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { icon: FaNewspaper, label: 'Posts Publicados', value: statsData.postsCount.toString(), trend: null, color: '#3b82f6' },
    { icon: FaEye, label: 'Visualizações', value: statsData.totalViews.toLocaleString('pt-BR'), trend: null, color: '#8b5cf6' },
    { icon: FaUsers, label: 'Leads', value: statsData.leadsCount.toString(), trend: null, color: '#10b981' },
    { icon: FaChartLine, label: 'Conversões', value: `${statsData.conversionRate}%`, trend: null, color: '#f59e0b' },
  ];

  const quickActions = [
    {
      icon: FaPlus,
      title: 'Novo Post',
      desc: 'Criar um novo artigo para o blog',
      link: '/admin/blog/novo',
      color: '#3b82f6'
    },
    {
      icon: FaUsers,
      title: 'Ver Leads',
      desc: 'Visualizar leads capturados',
      link: '/admin/leads',
      color: '#10b981'
    },
    {
      icon: FaChartLine,
      title: 'Analytics',
      desc: 'Ver métricas e relatórios',
      link: '/admin/analytics',
      color: '#8b5cf6'
    },
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    });
  };

  return (
    <Container>
      <WelcomeSection>
        <WelcomeTitle>Olá, {firstName}!</WelcomeTitle>
        <WelcomeText>Bem-vindo ao painel administrativo da Vega Robotics.</WelcomeText>
      </WelcomeSection>

      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index}>
            <StatHeader>
              <StatIcon $color={stat.color}>
                <stat.icon />
              </StatIcon>
              {stat.trend && (
                <StatTrend $positive={stat.trend > 0}>
                  {stat.trend > 0 ? '+' : ''}{stat.trend}%
                </StatTrend>
              )}
            </StatHeader>
            <StatValue>{stat.value}</StatValue>
            <StatLabel>{stat.label}</StatLabel>
          </StatCard>
        ))}
      </StatsGrid>

      <ContentGrid>
        <Card>
          <CardHeader>
            <CardTitle>Posts Recentes</CardTitle>
            <CardHeaderLink to="/admin/blog">
              Ver todos <FaArrowRight />
            </CardHeaderLink>
          </CardHeader>
          <CardContent>
            {loading ? (
              <EmptyState>
                <EmptyText>Carregando...</EmptyText>
              </EmptyState>
            ) : recentPosts.length === 0 ? (
              <EmptyState>
                <EmptyIcon>
                  <FaNewspaper />
                </EmptyIcon>
                <EmptyTitle>Nenhum post ainda</EmptyTitle>
                <EmptyText>
                  Comece criando seu primeiro post para o blog da Vega Robotics.
                </EmptyText>
                <CreateButton to="/admin/blog/novo">
                  <FaPlus />
                  Criar primeiro post
                </CreateButton>
              </EmptyState>
            ) : (
              <RecentPostsList>
                {recentPosts.map((post) => (
                  <RecentPostItem key={post.id} to={`/admin/blog/editar/${post.id}`}>
                    <PostThumbnail $image={post.cover_image} />
                    <PostDetails>
                      <div className="title">{post.title}</div>
                      <div className="meta">
                        <span>{formatDate(post.created_at)}</span>
                        <PostStatus $status={post.status}>
                          {post.status === 'published' ? 'Publicado' : 'Rascunho'}
                        </PostStatus>
                      </div>
                    </PostDetails>
                  </RecentPostItem>
                ))}
              </RecentPostsList>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <QuickActionsList>
              {quickActions.map((action, index) => (
                <QuickAction key={index} to={action.link}>
                  <QuickActionIcon $color={action.color}>
                    <action.icon />
                  </QuickActionIcon>
                  <QuickActionContent>
                    <div className="title">{action.title}</div>
                    <div className="desc">{action.desc}</div>
                  </QuickActionContent>
                  <QuickActionArrow>
                    <FaArrowRight />
                  </QuickActionArrow>
                </QuickAction>
              ))}
            </QuickActionsList>
          </CardContent>
        </Card>
      </ContentGrid>
    </Container>
  );
};

export default Dashboard;
